import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";

type VolunteerStatus =
  | "pending_review"
  | "approved"
  | "rejected"
  | "soft_deleted"
  | "superseded";

type VolunteerEntry = {
  id: string;
  schoolYear: string;
  familyId: string;
  familyName: string;
  familyCode?: string;
  sourceType: string;
  submittedByUid: string | null;
  submittedByEmail: string | null;
  submittedByName: string | null;
  serviceDateIso: string;
  activity: string;
  notes: string;
  requestedHours: number;
  approvedHours: number;
  status: VolunteerStatus;
  requiresApproval: boolean;
  deleted: boolean;
  reviewNote?: string | null;
};

function functionsClient() {
  return getFunctions(getApp(), "us-central1");
}

function num(v: any, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function statusLabel(v: VolunteerStatus) {
  switch (v) {
    case "pending_review":
      return "Pending";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    case "soft_deleted":
      return "Deleted";
    case "superseded":
      return "Superseded";
    default:
      return v;
  }
}

export function VolunteerReviewPage() {
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [schoolYear, setSchoolYear] = useState("");
  const [status, setStatus] = useState<"pending_review" | "approved" | "rejected" | "">("pending_review");
  const [entries, setEntries] = useState<VolunteerEntry[]>([]);

  const [reviewHoursById, setReviewHoursById] = useState<Record<string, string>>({});
  const [reviewNoteById, setReviewNoteById] = useState<Record<string, string>>({});
  const [editServiceDateById, setEditServiceDateById] = useState<Record<string, string>>({});
  const [editActivityById, setEditActivityById] = useState<Record<string, string>>({});
  const [editRequestedHoursById, setEditRequestedHoursById] = useState<Record<string, string>>({});
  const [editNotesById, setEditNotesById] = useState<Record<string, string>>({});
  const [editStatusById, setEditStatusById] = useState<Record<string, VolunteerStatus>>({});

  const visibleEntries = useMemo(() => entries, [entries]);

  function seedEntryState(list: VolunteerEntry[]) {
    setReviewHoursById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined || next[e.id] === "") {
          next[e.id] = String(e.status === "approved" ? e.approvedHours : e.requestedHours);
        }
      }
      return next;
    });

    setReviewNoteById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined) next[e.id] = e.reviewNote || "";
      }
      return next;
    });

    setEditServiceDateById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined) next[e.id] = e.serviceDateIso;
      }
      return next;
    });

    setEditActivityById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined) next[e.id] = e.activity;
      }
      return next;
    });

    setEditRequestedHoursById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined) next[e.id] = String(e.requestedHours);
      }
      return next;
    });

    setEditNotesById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined) next[e.id] = e.notes || "";
      }
      return next;
    });

    setEditStatusById((prev) => {
      const next = { ...prev };
      for (const e of list) {
        if (next[e.id] === undefined) next[e.id] = e.status;
      }
      return next;
    });
  }

  async function load() {
    setLoading(true);
    setErr(null);

    try {
      const fn = httpsCallable<
        { schoolYear?: string; status?: string },
        { ok: true; schoolYear: string; entries: VolunteerEntry[] }
      >(functionsClient(), "listVolunteerEntriesAdmin");

      const res = await fn({
        schoolYear: schoolYear || undefined,
        status: status || undefined,
      });

      setEntries(res.data.entries || []);
      setSchoolYear(res.data.schoolYear || schoolYear);
      seedEntryState(res.data.entries || []);
    } catch (e: any) {
      console.error("[VolunteerReviewPage] load failed", e);
      setErr(e?.message || "Failed to load volunteer entries.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function approve(entry: VolunteerEntry) {
    setWorkingId(entry.id);
    setErr(null);

    try {
      const fn = httpsCallable<
        { entryId: string; approvedHours?: number; reviewNote?: string },
        { ok: true; entry: VolunteerEntry }
      >(functionsClient(), "approveVolunteerEntry");

      const approvedHours = num(reviewHoursById[entry.id], entry.requestedHours);
      const reviewNote = (reviewNoteById[entry.id] || "").trim();

      const res = await fn({
        entryId: entry.id,
        approvedHours,
        reviewNote: reviewNote || undefined,
      });

      const updated = res.data.entry;

      setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      seedEntryState([updated]);
    } catch (e: any) {
      console.error("[VolunteerReviewPage] approve failed", e);
      setErr(e?.message || "Failed to approve entry.");
    } finally {
      setWorkingId(null);
    }
  }

  async function reject(entry: VolunteerEntry) {
    setWorkingId(entry.id);
    setErr(null);

    try {
      const fn = httpsCallable<
        { entryId: string; reviewNote?: string },
        { ok: true; entry: VolunteerEntry }
      >(functionsClient(), "rejectVolunteerEntry");

      const reviewNote = (reviewNoteById[entry.id] || "").trim();

      const res = await fn({
        entryId: entry.id,
        reviewNote: reviewNote || undefined,
      });

      const updated = res.data.entry;

      setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      seedEntryState([updated]);
    } catch (e: any) {
      console.error("[VolunteerReviewPage] reject failed", e);
      setErr(e?.message || "Failed to reject entry.");
    } finally {
      setWorkingId(null);
    }
  }

  async function saveEdit(entry: VolunteerEntry) {
    setWorkingId(entry.id);
    setErr(null);

    try {
      const fn = httpsCallable<
        {
          entryId: string;
          serviceDateIso: string;
          activity: string;
          requestedHours: number;
          approvedHours?: number;
          notes?: string;
          status: VolunteerStatus;
          reviewNote?: string;
        },
        { ok: true; entry: VolunteerEntry }
      >(functionsClient(), "updateVolunteerEntryAdmin");

      const payload = {
        entryId: entry.id,
        serviceDateIso: editServiceDateById[entry.id] || entry.serviceDateIso,
        activity: editActivityById[entry.id] || entry.activity,
        requestedHours: num(editRequestedHoursById[entry.id], entry.requestedHours),
        approvedHours: num(
          reviewHoursById[entry.id],
          entry.status === "approved" ? entry.approvedHours : entry.requestedHours
        ),
        notes: editNotesById[entry.id] || "",
        status: editStatusById[entry.id] || entry.status,
        reviewNote: (reviewNoteById[entry.id] || "").trim() || undefined,
      };

      const res = await fn(payload);
      const updated = res.data.entry;

      setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      seedEntryState([updated]);
    } catch (e: any) {
      console.error("[VolunteerReviewPage] saveEdit failed", e);
      setErr(e?.message || "Failed to update entry.");
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>Volunteer Review</h2>
        <div className="muted">
          Review public submissions and any family entries that require approval.
        </div>
        {err ? (
          <div style={{ marginTop: 12, color: "#dc2626", whiteSpace: "pre-wrap" }}>
            {err}
          </div>
        ) : null}
      </div>

      <div className="card">
        <h3>Filters</h3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(180px, 260px))", gap: 12 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            School Year
            <input
              className="input"
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              placeholder="2026-2027"
            />
          </label>

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Status
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
            >
              <option value="pending_review">Pending only</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="">All statuses</option>
            </select>
          </label>

          <div style={{ display: "flex", alignItems: "end", gap: 10 }}>
            <button className="btn" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Entries</h3>
        <div className="muted" style={{ marginBottom: 12 }}>
          {visibleEntries.length} entr{visibleEntries.length === 1 ? "y" : "ies"} loaded
        </div>

        {visibleEntries.length === 0 ? (
          <div className="muted">No volunteer entries matched the current filters.</div>
        ) : null}

        <div style={{ display: "grid", gap: 14 }}>
          {visibleEntries.map((entry) => {
            const pending = entry.status === "pending_review";
            const busy = workingId === entry.id;

            return (
              <div
                key={entry.id}
                style={{
                  border: "1px solid var(--border, #e5e7eb)",
                  borderRadius: 12,
                  padding: 14,
                  display: "grid",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 18 }}>{entry.familyName}</div>
                    <div className="muted">
                      {entry.serviceDateIso} · {entry.activity}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700 }}>{statusLabel(entry.status)}</div>
                    <div className="muted">
                      Requested: {entry.requestedHours.toFixed(2)} hrs
                    </div>
                    <div className="muted">
                      Approved: {entry.approvedHours.toFixed(2)} hrs
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(160px, 1fr))", gap: 10 }}>
                  <div className="muted">
                    <strong>Source:</strong> {entry.sourceType}
                  </div>
                  <div className="muted">
                    <strong>Family Code:</strong> {entry.familyCode || "—"}
                  </div>
                  <div className="muted">
                    <strong>School Year:</strong> {entry.schoolYear}
                  </div>
                  <div className="muted">
                    <strong>Submitted By:</strong> {entry.submittedByName || "—"}
                  </div>
                  <div className="muted">
                    <strong>Email:</strong> {entry.submittedByEmail || "—"}
                  </div>
                  <div className="muted">
                    <strong>Requires Approval:</strong> {entry.requiresApproval ? "Yes" : "No"}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
                  <label className="muted" style={{ display: "grid", gap: 6 }}>
                    Service Date
                    <input
                      className="input"
                      value={editServiceDateById[entry.id] ?? ""}
                      onChange={(e) =>
                        setEditServiceDateById((prev) => ({ ...prev, [entry.id]: e.target.value }))
                      }
                      placeholder="YYYY-MM-DD"
                      disabled={busy}
                    />
                  </label>

                  <label className="muted" style={{ display: "grid", gap: 6 }}>
                    Activity
                    <input
                      className="input"
                      value={editActivityById[entry.id] ?? ""}
                      onChange={(e) =>
                        setEditActivityById((prev) => ({ ...prev, [entry.id]: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </label>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "220px 220px 1fr", gap: 12 }}>
                  <label className="muted" style={{ display: "grid", gap: 6 }}>
                    Requested Hours
                    <input
                      className="input"
                      type="number"
                      min={0.25}
                      step="0.25"
                      value={editRequestedHoursById[entry.id] ?? ""}
                      onChange={(e) =>
                        setEditRequestedHoursById((prev) => ({ ...prev, [entry.id]: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </label>

                  <label className="muted" style={{ display: "grid", gap: 6 }}>
                    Approved Hours
                    <input
                      className="input"
                      type="number"
                      min={0}
                      step="0.25"
                      value={reviewHoursById[entry.id] ?? ""}
                      onChange={(e) =>
                        setReviewHoursById((prev) => ({ ...prev, [entry.id]: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </label>

                  <label className="muted" style={{ display: "grid", gap: 6 }}>
                    Status
                    <select
                      className="input"
                      value={editStatusById[entry.id] ?? entry.status}
                      onChange={(e) =>
                        setEditStatusById((prev) => ({
                          ...prev,
                          [entry.id]: e.target.value as VolunteerStatus,
                        }))
                      }
                      disabled={busy}
                    >
                      <option value="pending_review">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </label>
                </div>

                <label className="muted" style={{ display: "grid", gap: 6 }}>
                  Notes
                  <input
                    className="input"
                    value={editNotesById[entry.id] ?? ""}
                    onChange={(e) =>
                      setEditNotesById((prev) => ({ ...prev, [entry.id]: e.target.value }))
                    }
                    disabled={busy}
                  />
                </label>

                <label className="muted" style={{ display: "grid", gap: 6 }}>
                  Review Note
                  <input
                    className="input"
                    value={reviewNoteById[entry.id] ?? ""}
                    onChange={(e) =>
                      setReviewNoteById((prev) => ({ ...prev, [entry.id]: e.target.value }))
                    }
                    placeholder="Optional internal note"
                    disabled={busy}
                  />
                </label>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    className="btn"
                    disabled={!pending || busy}
                    onClick={() => approve(entry)}
                  >
                    {busy ? "Working…" : "Approve"}
                  </button>

                  <button
                    className="btn secondary"
                    disabled={!pending || busy}
                    onClick={() => reject(entry)}
                  >
                    {busy ? "Working…" : "Reject"}
                  </button>

                  <button
                    className="btn secondary"
                    disabled={busy}
                    onClick={() => saveEdit(entry)}
                  >
                    {busy ? "Working…" : "Save Changes"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}